import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button } from 'react-daisyui';

import { useAuthContext } from '@/context/AuthContext.tsx';
import useUserUpdate from '@/api/users/useUserUpdate.ts';
import OnboardingStep from '$/_const/OnboardingStep.ts';
import Routes from '@/router/Routes.ts';
import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/solid';
import OnboardingLogoutButton from '@/components/ui/OnboardingLogoutButton/OnboardingLogoutButton.tsx';
import { usePostHog } from '@posthog/react';

const OnboardingStartPage: FC = () => {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { logout, user } = useAuthContext();
  const navigate = useNavigate();
  const { mutateAsync: updateUser, isPending, error } = useUserUpdate();

  const handleGetStarted = () => {
    void (async () => {
      try {
        if (!user) return;

        await updateUser([{ onboarding_step: OnboardingStep.PREFERRED_NAME }, { id: user.id }]);

        posthog?.capture('onboarding_started');
        navigate(Routes.PREFERRED_NAME);
      } catch (error) {
        console.error(error);
        posthog?.capture('onboarding_step_failed', {
          onboarding_step: 'start',
        });
        posthog?.captureException(error, {
          operation: 'start_onboarding',
          onboarding_step: 'start',
        });
      }
    })();
  };

  return (
    <div className="flex min-h-[100dvh] w-full items-center justify-center bg-[var(--cream)] px-5 py-20">
      <OnboardingLogoutButton />
      <main className="flex w-full max-w-[650px] flex-col items-center text-center">
        {error && (
          <Alert className="mb-8 w-full font-sans text-sm" status="error">
            {error.message}
          </Alert>
        )}

        <p className="font-sans text-[11px] font-normal uppercase tracking-[0.24em] text-[var(--gold)]">
          {t('onboardingStartPage.header')}
        </p>

        <h1 className="mt-7 whitespace-pre-line font-serif text-[clamp(38px,4.5vw,48px)] font-semibold leading-[1.15] tracking-[-0.02em] text-[var(--ink)]">
          {t('onboardingStartPage.subheader')}
        </h1>

        <p className="mt-6 max-w-[610px] font-sans text-base font-normal leading-[1.6] text-[var(--text-muted)] sm:text-lg">
          {t('onboardingStartPage.description')}
        </p>

        <Button
          type="button"
          loading={isPending}
          onClick={handleGetStarted}
          className="journi-primary-button mt-16 min-h-0 w-full max-w-[420px] rounded-[9px] border-0 !bg-[var(--gold)] px-[34px] py-4 font-sans text-sm font-medium normal-case text-[var(--ink)] shadow-[0_8px_24px_rgba(180,146,58,0.16)] transition-[transform,background-color,box-shadow] duration-500 ease-[cubic-bezier(.22,1,.36,1)] hover:!-translate-y-0.5 hover:!bg-[var(--gold-lt)] hover:shadow-[0_12px_28px_rgba(180,146,58,0.24)] active:!translate-y-0 active:!bg-[var(--gold-deep)]"
        >
          <span>{t('button.letsGetStarted')}</span>
          <span aria-hidden="true">→</span>
        </Button>
        <Button
          onClick={logout}
          type="button"
          color="ghost"
          className="!gap-4 text-error font-medium"
          startIcon={<ArrowRightStartOnRectangleIcon className="w-4" />}
        >
          {t('button.logout')}
        </Button>
      </main>
    </div>
  );
};

export default OnboardingStartPage;
